import { Button } from 'components/button/Button/Button'
import DropdownListPortal from 'components/portal/DropdownListPortal/DropdownListPortal'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { DropDownListOptionType } from 'types/common/CommonType'
import { Util } from 'utils'
import { SearchListForm } from '../InputForm/SearchListForm/SearchListForm'
import { SelectListForm } from '../InputForm/SelectListForm/SelectListForm'
import { StyledDropdownList } from './style'
import { SelectFolderList } from '../SelectFolderList/SelectFolderList'
import { CustomScrollContainer } from 'components/scrollbar/CustomScollBarContainer/CustomScrollBarContainer'
import usePositionObserver from 'hooks/Observer/usePositionObserver'
import { getTranslation } from 'utils/translation'

/**
 * 드롭다운 리스트 컴포넌트 속성
 */
interface DropdownListProps {
  parentRef: React.RefObject<HTMLElement>
  searchFieldParentRef?: React.RefObject<HTMLElement>
  type: 'select' | 'search'
  size: 'large' | 'medium' | 'small'
  options: DropDownListOptionType[]
  value?: DropDownListOptionType[] | DropDownListOptionType
  folderValue?: DropDownListOptionType
  inputSearch?: string
  maxHeight?: string
  folderList?: DropDownListOptionType[]
  width?: number
  searchExclude?: string[]
  isSearchOption?: boolean
  noResultMessage?: string
  allSelectedMessage?: string
  enableAddOption?: boolean
  enableFontSelection?: boolean
  minLength?: number
  maxLength?: number
  direction?: 'left' | 'right'
  handleValue?: (selected: DropDownListOptionType) => void
  handleFolder?: (selected: DropDownListOptionType) => void
  handleListClose: () => void
  handleListClick?: (selected: DropDownListOptionType) => void
  handleListFocus?: (selected: DropDownListOptionType | null) => void
}

export interface RectType {
  left: number
  top: number
  width: number
  height: number
}

/**
 * DropdownList component
 * @author hc_yang
 * @param {DropdownListProps} param
 * @returns JSX.Element
 */
export const DropdownList = ({
  parentRef,
  searchFieldParentRef,
  type = 'select',
  size,
  options,
  value,
  folderValue,
  inputSearch,
  maxHeight = '20rem',
  width,
  folderList,
  searchExclude = [],
  minLength = 0,
  maxLength = 70,
  direction = 'left',
  handleValue,
  handleFolder,
  handleListClose,
  handleListClick,
  handleListFocus,
  isSearchOption = false,
  noResultMessage = getTranslation('message', 'INFO.NO_RESULT_FOUND'),
  allSelectedMessage = getTranslation('message', 'INFO.ALL_ITEMS_SELECTED'),
  enableAddOption = false,
  enableFontSelection = false,
}: DropdownListProps) => {
  const listAreaRef = useRef<HTMLDivElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const [searchFieldRect, setSearchFieldRect] = useState({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  })
  const [parentBoxRect, setParentBoxRect] = useState<RectType>()
  const [dropdownPosition, setDropdownPosition] = useState<'top' | 'bottom'>('bottom')
  const [searchValue, setSearchValue] = useState<string>('')
  const [filterOptions, setFilterOptions] = useState<DropDownListOptionType[]>(options)
  const [targetOptionIndex, setTargetOptionIndex] = useState(-1)
  const { rect: parentRect } = usePositionObserver(parentRef)

  const calculateListHeight = () => {
    const baseHeight = size === 'small' ? 18 : size === 'medium' ? 28 : 38
    const listHeight = options.length * baseHeight

    const maxHeightPx = Util.convertCssUnitToPx(maxHeight)
    if (maxHeightPx !== null && listHeight > maxHeightPx) {
      return maxHeightPx
    }
    return listHeight
  }

  const handleTargetOptionIndex = (e: KeyboardEvent, add: boolean) => {
    let index = targetOptionIndex
    let isFocusing = false
    while (!isFocusing) {
      if (!add && targetOptionIndex > 0) {
        index -= 1
      }
      if (add && targetOptionIndex < options.length - 1) {
        index += 1
      }

      if (!add && targetOptionIndex <= 0) {
        index = -1
        isFocusing = false
        break
      }
      if (!options?.[index]?.disabled && !options?.[index]?.allSelection) {
        isFocusing = true
        break
      }
    }

    e.preventDefault()
    e.stopPropagation()
    setTargetOptionIndex(index)
    scrollIntoView(index)
  }

  const scrollIntoView = (index: number) => {
    const dropdown = listAreaRef.current
    if (!dropdown) return
    const activeItem = dropdown.children.item(index)

    if (activeItem) {
      // 옵션 하나당 높이
      const itemHeight = activeItem.clientHeight
      // dropdown 전체의 높이 + 아래로 스크롤한 만큼의 높이
      const dropdownHeight = dropdown.scrollTop + dropdown.clientHeight
      // 현재 focus하고 있는 옵션으로부터 최상단까지의 높이
      const itemOffset = itemHeight * index

      if (dropdownHeight <= itemOffset + itemHeight) {
        dropdown.scrollTop += itemHeight
      }
      if (itemOffset <= dropdown.scrollTop) {
        dropdown.scrollTop = itemOffset
      }
    }
  }

  const handleSearchValueAddButton = () => {
    const searchIndex = options.findIndex((option) => option.label === searchValue)
    const selectedValue =
      searchIndex === -1 ? { label: searchValue, value: searchValue } : options[searchIndex]
    if (handleValue) handleValue(selectedValue)
  }

  const keydownHandler = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      handleTargetOptionIndex(e, true)
    }
    if (e.key === 'ArrowUp') {
      handleTargetOptionIndex(e, false)
    }
    if (e.key === 'Enter') {
      if (targetOptionIndex !== -1) {
        handleValue && handleValue(filterOptions[targetOptionIndex])
      }
    }
  }

  const getAllSelectOptionCheckedState = (option: DropDownListOptionType): boolean => {
    if (option.allSelection && Array.isArray(value)) {
      const filterOptions = options.filter((option) => !option.disabled && !option.allSelection)
      if (value.length === filterOptions.length) return true
    }
    return false
  }

  const handleListClickValue = (selected: DropDownListOptionType) => {
    if (handleValue) handleValue(selected)
    if (handleListClick) handleListClick(selected)
  }

  const updateDropDownPosition = () => {
    if (searchFieldParentRef?.current) {
      setSearchFieldRect(searchFieldParentRef.current.getBoundingClientRect())
    }
    if (parentRef.current) {
      const rect = parentRef.current.getBoundingClientRect()
      setParentBoxRect(rect)
      const listHeight = calculateListHeight()
      // list 표출 기준 부모요소의 하단 위치 + 표출될 리스트의 높이 + 리스트와 부모 요소의 간격 2px
      const listBottomOffset = rect.bottom + listHeight + 2
      // 화면 높이와 요소의 위치를 비교하여 잘리는지 체크
      if (listBottomOffset > window.innerHeight) {
        setDropdownPosition('top')
      } else {
        setDropdownPosition('bottom')
      }
    }
  }

  const calculatedLeft = useMemo(() => {
    if (!parentBoxRect) return 0

    if (direction === 'right' && width) {
      // width가 없는 경우에는 부모 요소의 너비를 기준으로 계산하므로 position이 의미가 없음
      // 따라서 direction이 right이고, width가 있는 경우에만 별개로 값을 계산
      return parentBoxRect.left - (width - parentBoxRect.width)
    } else {
      return parentBoxRect.left
    }
  }, [width, parentBoxRect])

  useEffect(() => {
    return () => handleListFocus && handleListFocus(null)
  }, [])

  useEffect(() => {
    if (handleListFocus) {
      if (targetOptionIndex === -1) handleListFocus(null)
      else handleListFocus(filterOptions[targetOptionIndex])
    }
  }, [targetOptionIndex])

  useEffect(() => {
    window.addEventListener('keydown', keydownHandler)
    return () => {
      window.removeEventListener('keydown', keydownHandler)
    }
  }, [targetOptionIndex, listAreaRef, options])

  useEffect(() => {
    setTargetOptionIndex(-1)
    if (inputSearch || searchValue) {
      setFilterOptions(
        options.filter(
          (option) =>
            option.label
              .toLowerCase()
              .includes(inputSearch?.toLowerCase() || searchValue.toLowerCase()) &&
            !option.allSelection &&
            !searchExclude.includes(option.label)
        )
      )
    } else {
      setFilterOptions(options)
    }
  }, [searchValue, inputSearch, value])

  useEffect(() => {
    if (folderValue) {
      setFilterOptions(
        folderValue.isAll
          ? options
          : options.filter((option) => option.folderKey?.includes(folderValue.value as string))
      )
    }
  }, [folderValue])

  useEffect(() => {
    updateDropDownPosition()
  }, [parentRect, filterOptions])

  useEffect(() => {
    if (searchInputRef.current) searchInputRef.current.focus()
  }, [searchInputRef.current])

  return (
    <DropdownListPortal onClose={handleListClose}>
      <StyledDropdownList
        $size={size}
        $maxHeight={maxHeight}
        $direction={dropdownPosition}
        $searchFieldHeight={
          isSearchOption
            ? size === 'large'
              ? '5rem'
              : size === 'medium'
                ? '3.8rem'
                : '3rem'
            : '0rem'
        }
        // width를 커스텀하여 설정한 경우, 리스트의 너비가 넓어서 잘리는 경우가 생기므로 아래와 같이 left 값을 계산
        style={{
          pointerEvents: 'none',
          position: 'fixed',
          ...(parentBoxRect
            ? {
                width: (width ? width : parentBoxRect.width) + 'px',
                height: parentBoxRect.height + 'px',
                left: calculatedLeft + 'px',
                top: parentBoxRect.top + 'px',
                zIndex: '100000',
                display: 'block',
              }
            : {
                display: 'none',
              }),
        }}
      >
        <div className="dropdown-list">
          {isSearchOption && searchFieldParentRef?.current && (
            <input
              ref={searchInputRef}
              style={{
                position: 'fixed',
                width: searchFieldRect.width + 'px',
                height: searchFieldRect.height + 'px',
                left: searchFieldRect.left + 'px',
                top: searchFieldRect.top + 'px',
                zIndex: '9000',
              }}
              type="text"
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search"
              className="search-input"
              minLength={minLength}
              maxLength={maxLength}
            />
          )}
          {enableAddOption && searchValue && (
            <div
              style={{
                display: 'flex',
                padding: '0.8rem',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {searchValue}
              <Button
                color="primary"
                type="text"
                size="small"
                label="ADD"
                onClick={() => {
                  handleSearchValueAddButton()
                }}
              ></Button>
            </div>
          )}
          {folderList && folderValue && (
            <SelectFolderList
              size={size}
              options={folderList}
              value={folderValue}
              handleValue={handleFolder}
            />
          )}

          <div className="list-area" ref={listAreaRef}>
            {filterOptions.length > 0 && (
              <CustomScrollContainer
                width="100%"
                height="max-content"
                maxHeight={
                  folderList
                    ? `calc(${maxHeight} - ${
                        size === 'large' ? 40 : size === 'medium' ? 28 : 20
                      }px - 9px)`
                    : `calc(${maxHeight} - 9px)`
                }
              >
                {type === 'select' &&
                  filterOptions.map((option, idx) => (
                    <SelectListForm
                      key={`${option.label}-dropdown-option-${idx}`}
                      isFocused={targetOptionIndex === idx}
                      size={size}
                      option={option}
                      value={value}
                      showIndent={folderList && folderValue ? true : false}
                      handleValue={handleListClickValue}
                      searchValue={searchValue}
                      forceCheck={getAllSelectOptionCheckedState(option)}
                      enableFontSelection={enableFontSelection}
                    />
                  ))}
                {type === 'search' &&
                  filterOptions.map((option, idx) => (
                    <SearchListForm
                      key={`${option.label}-dropdown-option-${idx}`}
                      isFocused={targetOptionIndex === idx}
                      size={size}
                      option={option}
                      showIndent={folderList && folderValue ? true : false}
                      handleValue={handleListClickValue}
                      searchValue={inputSearch || searchValue}
                      enableFontSelection={enableFontSelection}
                    />
                  ))}
              </CustomScrollContainer>
            )}

            {!filterOptions.length && (
              <div className="message">
                <p className="label">
                  {Array.isArray(value) && value.length && !searchValue
                    ? allSelectedMessage
                    : noResultMessage}
                </p>
              </div>
            )}
          </div>
        </div>
      </StyledDropdownList>
    </DropdownListPortal>
  )
}
